import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProduct, createProduct, updateProduct } from '../lib/api';
import { ArrowLeft, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

function FormSection({ title, children }) {
    return (
        <div className="bg-card border border-border rounded-lg">
            <div className="px-5 py-3.5 border-b border-border">
                <h3 className="font-semibold text-sm">{title}</h3>
            </div>
            <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    {children}
                </div>
            </div>
        </div>
    );
}

function FormField({ label, name, value, onChange, type = 'text', placeholder, disabled, className = '', mono = false }) {
    return (
        <div className={className}>
            <Label htmlFor={name} className="text-sm text-muted-foreground mb-1.5 block">{label}</Label>
            <Input
                id={name}
                name={name}
                type={type}
                value={value ?? ''}
                onChange={onChange}
                placeholder={placeholder || label}
                disabled={disabled}
                className={mono ? 'font-mono' : ''}
                data-testid={`field-${name}`}
            />
        </div>
    );
}

function TextAreaField({ label, name, value, onChange, placeholder, rows = 3 }) {
    return (
        <div className="md:col-span-2">
            <Label htmlFor={name} className="text-sm text-muted-foreground mb-1.5 block">{label}</Label>
            <textarea
                id={name}
                name={name}
                value={value ?? ''}
                onChange={onChange}
                placeholder={placeholder || label}
                rows={rows}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid={`field-${name}`}
            />
        </div>
    );
}

export default function ProductForm() {
    const { productKey } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(productKey);
    
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    
    // Product master fields
    const [form, setForm] = useState({
        product_key: '',
        display_sku: '',
        product_name: '',
        brand: '',
        category: '',
        subcategory: '',
        decor_no: '',
        decor_name: '',
        decor_primary: '',
        decor_options: '',
        finish: '',
        shape: '',
        description: '',
        status: 'active',
        internal_remarks: '',
        variant_group: '',
        unit_of_sale: '',
        source_sheet: '',
    });
    
    // Specs fields
    const [specs, setSpecs] = useState({
        size_mm: '',
        length_mm: '',
        width_mm: '',
        thickness_mm: '',
        thickness_text: '',
        finish: '',
        type: '',
        material_type: '',
        groove_count: '',
        bowl_count: '',
        bowl_type: '',
        top_sheet: '',
        handle_type: '',
        stand_material: '',
        stand_size_mm: '',
        board_size_mm: '',
        working_size_mm: '',
        no_of_boards: '',
        special_features: '',
        care_instructions: '',
        usage_notes: '',
    });
    
    // Packaging fields
    const [pkg, setPkg] = useState({
        unit_weight_g: '',
        unit_weight_kg: '',
        packing_type: '',
        packaging_status: '',
        inner_qty: '',
        carton_qty: '',
        gross_weight_kg: '',
        net_weight_kg: '',
        cbm: '',
        notes: '',
        fragile_notes: '',
    });

    useEffect(() => {
        if (!isEdit) return;
        const fetchProduct = async () => {
            try {
                const res = await getProduct(productKey);
                const p = res.data;
                
                // Populate form
                setForm({
                    product_key: p.product_key || '',
                    display_sku: p.display_sku || '',
                    product_name: p.product_name || '',
                    brand: p.brand || '',
                    category: p.category || '',
                    subcategory: p.subcategory || '',
                    decor_no: p.decor_no || '',
                    decor_name: p.decor_name || '',
                    decor_primary: p.decor_primary || '',
                    decor_options: p.decor_options || '',
                    finish: p.finish || '',
                    shape: p.shape || '',
                    description: p.description || '',
                    status: p.status || 'active',
                    internal_remarks: p.internal_remarks || '',
                    variant_group: p.variant_group || '',
                    unit_of_sale: p.unit_of_sale || '',
                    source_sheet: p.source_sheet || '',
                });
                
                // Populate specs
                const s = p.specifications || {};
                setSpecs({
                    size_mm: s.size_mm || '',
                    length_mm: s.length_mm ?? '',
                    width_mm: s.width_mm ?? '',
                    thickness_mm: s.thickness_mm ?? '',
                    thickness_text: s.thickness_text || '',
                    finish: s.finish || '',
                    type: s.type || '',
                    material_type: s.material_type || '',
                    groove_count: s.groove_count ?? '',
                    bowl_count: s.bowl_count ?? '',
                    bowl_type: s.bowl_type || '',
                    top_sheet: s.top_sheet || '',
                    handle_type: s.handle_type || '',
                    stand_material: s.stand_material || '',
                    stand_size_mm: s.stand_size_mm || '',
                    board_size_mm: s.board_size_mm || '',
                    working_size_mm: s.working_size_mm || '',
                    no_of_boards: s.no_of_boards ?? '',
                    special_features: s.special_features || '',
                    care_instructions: s.care_instructions || '',
                    usage_notes: s.usage_notes || '',
                });
                
                // Populate packaging
                const pk = p.packaging || {};
                setPkg({
                    unit_weight_g: pk.unit_weight_g ?? '',
                    unit_weight_kg: pk.unit_weight_kg ?? '',
                    packing_type: pk.packing_type || '',
                    packaging_status: pk.packaging_status || '',
                    inner_qty: pk.inner_qty ?? '',
                    carton_qty: pk.carton_qty ?? '',
                    gross_weight_kg: pk.gross_weight_kg ?? '',
                    net_weight_kg: pk.net_weight_kg ?? '',
                    cbm: pk.cbm ?? '',
                    notes: pk.notes || pk.packaging_notes || '',
                    fragile_notes: pk.fragile_notes || '',
                });
            } catch (error) {
                toast.error('Failed to load product');
                navigate('/products');
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [productKey, isEdit, navigate]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSpecsChange = (e) => {
        const { name, value } = e.target;
        setSpecs(prev => ({ ...prev, [name]: value }));
    };
    
    const handlePkgChange = (e) => {
        const { name, value } = e.target;
        setPkg(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.product_key.trim()) {
            toast.error('Product Key is required');
            return;
        }
        
        setSaving(true);
        try {
            // Clean numeric fields in specs
            const cleanSpecs = { ...specs };
            ['length_mm', 'width_mm', 'thickness_mm'].forEach(f => {
                cleanSpecs[f] = cleanSpecs[f] !== '' ? parseFloat(cleanSpecs[f]) || null : null;
            });
            ['groove_count', 'bowl_count', 'no_of_boards'].forEach(f => {
                cleanSpecs[f] = cleanSpecs[f] !== '' ? parseInt(cleanSpecs[f]) || null : null;
            });
            
            // Clean numeric fields in packaging
            const cleanPkg = { ...pkg };
            ['unit_weight_g', 'unit_weight_kg', 'gross_weight_kg', 'net_weight_kg', 'cbm'].forEach(f => {
                cleanPkg[f] = cleanPkg[f] !== '' ? parseFloat(cleanPkg[f]) || null : null;
            });
            ['inner_qty', 'carton_qty'].forEach(f => {
                cleanPkg[f] = cleanPkg[f] !== '' ? parseInt(cleanPkg[f]) || null : null;
            });
            
            const payload = {
                ...form,
                specifications: cleanSpecs,
                packaging: cleanPkg,
            };
            
            if (isEdit) {
                await updateProduct(productKey, payload);
                toast.success('Product updated');
                navigate(`/products/${productKey}`);
            } else {
                await createProduct(payload);
                toast.success('Product created');
                navigate(`/products/${form.product_key}`);
            }
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to save product';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-5xl" data-testid="product-form-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
                        <Link to={isEdit ? `/products/${productKey}` : '/products'}>
                            <ArrowLeft size={16} className="mr-1" /> Back
                        </Link>
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight" data-testid="form-title">
                        {isEdit ? `Edit: ${form.display_sku || form.product_key}` : 'Add New Product'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Core Product Info */}
                <FormSection title="Product Information">
                    <FormField label="Product Key" name="product_key" value={form.product_key} onChange={handleFormChange} disabled={isEdit} mono placeholder="e.g. CB01_1_150_X_230_10MM_959" />
                    <FormField label="Display SKU" name="display_sku" value={form.display_sku} onChange={handleFormChange} mono />
                    <FormField label="Product Name" name="product_name" value={form.product_name} onChange={handleFormChange} className="md:col-span-2" />
                    <FormField label="Brand" name="brand" value={form.brand} onChange={handleFormChange} />
                    <div>
                        <Label htmlFor="status" className="text-sm text-muted-foreground mb-1.5 block">Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger data-testid="field-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="inactive">Inactive</SelectItem>
                                <SelectItem value="under_development">Under Development</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <FormField label="Category" name="category" value={form.category} onChange={handleFormChange} />
                    <FormField label="Sub-category" name="subcategory" value={form.subcategory} onChange={handleFormChange} />
                    <FormField label="Decor No" name="decor_no" value={form.decor_no} onChange={handleFormChange} mono />
                    <FormField label="Decor Name" name="decor_name" value={form.decor_name} onChange={handleFormChange} />
                    <FormField label="Finish" name="finish" value={form.finish} onChange={handleFormChange} />
                    <FormField label="Shape" name="shape" value={form.shape} onChange={handleFormChange} />
                    <FormField label="Variant Group" name="variant_group" value={form.variant_group} onChange={handleFormChange} mono />
                    <FormField label="Unit of Sale" name="unit_of_sale" value={form.unit_of_sale} onChange={handleFormChange} />
                    <TextAreaField label="Description" name="description" value={form.description} onChange={handleFormChange} />
                    <TextAreaField label="Internal Remarks" name="internal_remarks" value={form.internal_remarks} onChange={handleFormChange} />
                </FormSection>

                {/* Specifications */}
                <FormSection title="Specifications">
                    <FormField label="Size (mm)" name="size_mm" value={specs.size_mm} onChange={handleSpecsChange} />
                    <FormField label="Length (mm)" name="length_mm" value={specs.length_mm} onChange={handleSpecsChange} type="number" mono />
                    <FormField label="Width (mm)" name="width_mm" value={specs.width_mm} onChange={handleSpecsChange} type="number" mono />
                    <FormField label="Thickness (mm)" name="thickness_mm" value={specs.thickness_mm} onChange={handleSpecsChange} type="number" mono />
                    <FormField label="Thickness Text" name="thickness_text" value={specs.thickness_text} onChange={handleSpecsChange} />
                    <FormField label="Finish" name="finish" value={specs.finish} onChange={handleSpecsChange} />
                    <FormField label="Type" name="type" value={specs.type} onChange={handleSpecsChange} />
                    <FormField label="Material Type" name="material_type" value={specs.material_type} onChange={handleSpecsChange} />
                    <FormField label="Groove Count" name="groove_count" value={specs.groove_count} onChange={handleSpecsChange} type="number" mono />
                    <FormField label="Bowl Count" name="bowl_count" value={specs.bowl_count} onChange={handleSpecsChange} type="number" mono />
                    <FormField label="Bowl Type" name="bowl_type" value={specs.bowl_type} onChange={handleSpecsChange} />
                    <FormField label="Top Sheet" name="top_sheet" value={specs.top_sheet} onChange={handleSpecsChange} />
                    <FormField label="Handle Type" name="handle_type" value={specs.handle_type} onChange={handleSpecsChange} />
                    <FormField label="Stand Material" name="stand_material" value={specs.stand_material} onChange={handleSpecsChange} />
                    <FormField label="No. of Boards" name="no_of_boards" value={specs.no_of_boards} onChange={handleSpecsChange} type="number" mono />
                    <TextAreaField label="Special Features" name="special_features" value={specs.special_features} onChange={handleSpecsChange} />
                    <TextAreaField label="Care Instructions" name="care_instructions" value={specs.care_instructions} onChange={handleSpecsChange} />
                    <TextAreaField label="Usage Notes" name="usage_notes" value={specs.usage_notes} onChange={handleSpecsChange} />
                </FormSection>

                {/* Packaging */}
                <FormSection title="Packaging">
                    <FormField label="Unit Weight (g)" name="unit_weight_g" value={pkg.unit_weight_g} onChange={handlePkgChange} type="number" mono />
                    <FormField label="Unit Weight (kg)" name="unit_weight_kg" value={pkg.unit_weight_kg} onChange={handlePkgChange} type="number" mono />
                    <FormField label="Packing Type" name="packing_type" value={pkg.packing_type} onChange={handlePkgChange} />
                    <FormField label="Packaging Status" name="packaging_status" value={pkg.packaging_status} onChange={handlePkgChange} />
                    <FormField label="Inner Qty" name="inner_qty" value={pkg.inner_qty} onChange={handlePkgChange} type="number" mono />
                    <FormField label="Carton Qty" name="carton_qty" value={pkg.carton_qty} onChange={handlePkgChange} type="number" mono />
                    <FormField label="Net Weight (kg)" name="net_weight_kg" value={pkg.net_weight_kg} onChange={handlePkgChange} type="number" mono />
                    <FormField label="Gross Weight (kg)" name="gross_weight_kg" value={pkg.gross_weight_kg} onChange={handlePkgChange} type="number" mono />
                    <FormField label="CBM" name="cbm" value={pkg.cbm} onChange={handlePkgChange} type="number" mono />
                    <TextAreaField label="Packaging Notes" name="notes" value={pkg.notes} onChange={handlePkgChange} />
                    <TextAreaField label="Fragile/Handling Notes" name="fragile_notes" value={pkg.fragile_notes} onChange={handlePkgChange} />
                </FormSection>

                {/* Submit */}
                <div className="flex items-center gap-3 pt-2">
                    <Button type="submit" disabled={saving} data-testid="save-product-btn">
                        <FloppyDisk size={18} className="mr-2" />
                        {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Product'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate(isEdit ? `/products/${productKey}` : '/products')}>
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    );
}
